import { asOptionalString } from "@aix/jsutils/src/coerce"
import { emit, flush } from "@aix/jsutils/src/axiom-events/emit"
import { LOG_REDACTED, sanitizeLogRecord, sanitizeLogValue } from "@aix/jsutils/src/axiom-events/sanitize"
import type { LogString, SafeLogValue } from "@aix/jsutils/src/axiom-events/sensitive"

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

// Safe process.env access (Vite replaces process.env but bare process access crashes in production)
const safeGetEnv = (key: string): string | undefined => {
  if ((globalThis as any).process?.env) {
    return process.env[key]
  }
  return undefined
}

// Default to INFO level if not set
const currentLogLevel = (safeGetEnv("NEXT_PUBLIC_LOG_LEVEL") || "INFO") as keyof typeof LogLevel
const LOG_LEVEL = LogLevel[currentLogLevel] ?? LogLevel.DEBUG

// Persistent context added via enrichLoggingWithParams
let persistentContext: Record<string, unknown> = {}

// Browser context (captured once at init)
const browserContext =
  typeof window !== "undefined"
    ? {
        userAgent: window.navigator.userAgent,
        language: window.navigator.language,
        platform: window.navigator.platform,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
      }
    : undefined

export interface FrontendLoggingContext {
  path: string
  fullUrl: string
  query: any
  pathname: string

  userId?: string
  userEmail?: string

  // Note: should be used sparingly as this will override the existing misc
  misc?: any
}

export const enrichLoggingWithParams = (params: FrontendLoggingContext) => {
  persistentContext = {
    ...persistentContext,
    ...params,
  }
}

export const logFlush = async () => {
  await flush()
}

const shouldLog = (level: LogLevel): boolean => {
  return level <= LOG_LEVEL
}

const getCallerInfo = () => {
  // Create an Error to capture the stack trace
  const err = new Error()

  // Get the stack trace and split it into lines
  const stack = err.stack?.split("\n")

  if (!stack) return null

  // Skip first 3 lines (Error creation, getCallerInfo, and the logger function)
  // Format: "    at FunctionName (file:line:column)"
  const callerLine = stack[3]

  if (!callerLine) return null

  // Extract function name and location
  const match = /at\s+(.*)\s+\((.*):(\d+):(\d+)\)/.exec(callerLine)
  if (!match) {
    // Handle anonymous functions
    const anonMatch = /at\s+(.*):(\d+):(\d+)/.exec(callerLine)
    if (!anonMatch) return null

    return {
      function: "<anonymous>",
      file: anonMatch[1],
      line: anonMatch[2],
      column: anonMatch[3],
    }
  }

  return {
    function: match[1],
    file: match[2],
    line: match[3],
    column: match[4],
  }
}

/**
 * Emit a frontend log event through the standardized emit() system.
 * All context goes into the `data` envelope (map field) to prevent Axiom column explosion.
 */
const emitFrontendLog = (level: "info" | "debug" | "warn" | "error", message: string, context?: unknown) => {
  const callerInfo = getCallerInfo()
  const snapshot = sanitizeLogValue(context)
  let data: Record<string, unknown> = {}
  if (snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) data = snapshot
  else if (typeof snapshot === "number" || typeof snapshot === "boolean") data = { contextValue: snapshot }
  else if (context !== undefined && context !== null) data = { contextMsg: LOG_REDACTED }

  const contextData: Record<string, unknown> = {
    ...data,
    ...sanitizeLogRecord(persistentContext),
    caller: callerInfo,
    browser: browserContext,
    timestamp: new Date().toISOString(),
  }

  // Add performance info for info-level logs
  if (level === "info" && typeof window !== "undefined") {
    contextData.performance = {
      loadTime: window.performance?.timing?.loadEventEnd - window.performance?.timing?.navigationStart,
      domContentLoaded:
        window.performance?.timing?.domContentLoadedEventEnd - window.performance?.timing?.navigationStart,
    }
  }

  emit({
    ...contextData,
    type: "log",
    level,
    message,
    subtype: "frontend",
    userId: asOptionalString(contextData.userId),
  })
}

/**
 * flg - Frontend log
 *
 * Uses the standardized emit() system from @aix/jsutils.
 * All arbitrary context is nested under the `data` map field
 * to prevent Axiom schema explosion.
 */
const frontendLogAtLevel =
  (level: LogLevel, name: "info" | "debug" | "warn" | "error") =>
  <Args extends [unknown?]>(input: LogString, ...args: Args & SafeLogValue<Args>): void => {
    if (!shouldLog(level)) return
    emitFrontendLog(name, input, args[0])
  }

export const flg = {
  info: frontendLogAtLevel(LogLevel.INFO, "info"),
  debug: frontendLogAtLevel(LogLevel.DEBUG, "debug"),
  warn: frontendLogAtLevel(LogLevel.WARN, "warn"),
  error: frontendLogAtLevel(LogLevel.ERROR, "error"),
}
